import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Footer.scss';

const footerColumns = [
  {
    titleKey: 'footer.sections.happening',
    links: [
      { key: 'footer.links.happeningAll',         href: '/happening'              },
      { key: 'footer.links.happeningEvents',       href: '/happening/events'       },
      { key: 'footer.links.happeningExperiences',  href: '/happening/experiences'  },
    ],
  },
  {
    titleKey: 'footer.sections.work',
    links: [
      { key: 'footer.links.workLiving',     href: '/work/living'     },
      { key: 'footer.links.workHealthcare', href: '/work/healthcare' },
      { key: 'footer.links.workTravel',     href: '/work/travel'     },
    ],
  },
  {
    titleKey: 'footer.sections.study',
    links: [
      { key: 'footer.links.studyEducation', href: '/study/education' },
      { key: 'footer.links.studyColleges',  href: '/study/colleges'  },
    ],
  },
  {
    titleKey: 'footer.sections.invest',
    links: [
      { key: 'footer.links.investAbout',   href: '/invest/about'    },
      { key: 'footer.links.investSectors', href: '/invest/sectors'  },
      { key: 'footer.links.investSetup',   href: '/invest/business' },
    ],
  },
  {
    titleKey: 'footer.sections.shop',
    links: [
      { key: 'footer.links.shopNew',     href: '/shop/new'            },
      { key: 'footer.links.shopBest',    href: '/shop/best'           },
      { key: 'footer.links.shopCollabs', href: '/shop/collaborations' },
    ],
  },
  {
    titleKey: 'footer.sections.partners',
    links: [
      { key: 'footer.links.partnersMedia',    href: '/partners/media-kit' },
      { key: 'footer.links.partnersPress',    href: '/partners/press'     },
      { key: 'footer.links.partnersContact',  href: '/partners/contact'   },
      { key: 'footer.links.partnersSecurity', href: '/partners/security'  },
    ],
  },
];

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    setEmail('');
  };

  return (
    <footer className="footer">
      {/* Newsletter */}
      <div className="footer__newsletter">
        <div className="footer__container">
          <h2 className="footer__newsletter-title">{t('footer.newsletter.title')}</h2>
          <form className="footer__newsletter-form" onSubmit={handleSubscribe}>
            <div className="footer__input-wrap">
              <svg className="footer__search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="#aaa" strokeWidth="2" />
                <path d="M16.5 16.5L21 21" stroke="#aaa" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                type="email"
                className="footer__input"
                placeholder={t('footer.newsletter.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="footer__subscribe-btn">{t('footer.newsletter.button')}</button>
          </form>
        </div>
      </div>

      {/* Links grid */}
      <div className="footer__links">
        <div className="footer__container">
          <div className="footer__grid">
            {footerColumns.map((col) => (
              <div key={col.titleKey} className="footer__col">
                <h4 className="footer__col-title">{t(col.titleKey)}</h4>
                <ul className="footer__col-list">
                  {col.links.map((link) => (
                    <li key={link.href}>
                      <a href={link.href} className="footer__col-link">{t(link.key)}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="footer__bottom">
        <div className="footer__container footer__bottom-inner">
          <p className="footer__copy">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
          <div className="footer__socials">
            <a href="https://www.youtube.com/@YourSharjah" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label={t('footer.social.youtube')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1C4.5 20.4 12 20.4 12 20.4s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/>
              </svg>
            </a>
            <a href="https://www.facebook.com/yoursharjah" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label={t('footer.social.facebook')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/yoursharjah" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label={t('footer.social.instagram')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
              </svg>
            </a>
            <a href="https://www.snapchat.com/add/yoursharjah" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label={t('footer.social.snapchat')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.08.332-.13.49-.13.21 0 .45.065.63.186.338.215.52.511.528.797.007.254-.123.49-.356.66-.14.1-.381.195-.5.237-.18.065-.336.13-.451.193-.12.065-.21.12-.255.165.045.15.085.3.13.45.193.72.46 1.5.927 2.25.347.571.73.96 1.275 1.245.18.09.39.15.571.18.12.03.24.065.36.09l.165.045c.195.045.375.12.525.21.375.24.585.615.585.99 0 .345-.195.675-.525.93-.255.195-.6.33-.99.404-.21.045-.404.12-.59.21-.14.06-.255.105-.36.105-.075 0-.15-.03-.239-.06-.27-.09-.596-.15-1.005-.15-.225 0-.45.015-.675.045-.45.06-.84.27-1.17.56-.556.5-1.11.992-1.875 1.43-1.139.636-2.34.94-3.63.94-1.29 0-2.491-.315-3.63-.94-.764-.438-1.318-.93-1.875-1.43-.33-.29-.72-.5-1.17-.56a7.18 7.18 0 0 0-.675-.045c-.409 0-.735.06-1.005.15-.09.03-.164.06-.24.06-.104 0-.224-.045-.359-.105-.186-.09-.375-.165-.585-.21-.39-.075-.735-.21-.99-.405-.33-.255-.525-.585-.525-.93 0-.375.21-.75.585-.99.15-.09.33-.165.525-.21l.165-.045c.12-.03.24-.06.36-.09.18-.03.39-.09.57-.18.546-.285.93-.674 1.275-1.245.466-.75.733-1.53.926-2.25.045-.15.085-.3.13-.45-.046-.045-.135-.1-.255-.165-.12-.06-.27-.128-.45-.193-.12-.042-.36-.137-.5-.238-.234-.17-.363-.405-.357-.66.008-.286.19-.582.53-.797.18-.12.42-.186.63-.186.158 0 .325.05.49.13.374.181.732.285 1.032.301.199 0 .327-.045.401-.09-.008-.165-.018-.33-.03-.51l-.003-.06c-.104-1.628-.23-3.654.3-4.847C7.858 1.07 11.215.793 12.206.793z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
